import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ArrowClockwise,
  SignOut,
  WifiHigh,
  WifiSlash,
} from '@phosphor-icons/react'
import { apiRequest } from '../api/client'
import type { AuthenticatedUser } from '../api/types'
import { navigate, usePathname } from '../app/navigation'
import { clearVerifiedOfflineSession } from '../app/offlineSession'
import { Link } from './Link'
import { queryClient } from '../app/queryClient'

const navItems = [
  { to: '/', number: '01', label: 'Dnes', shortLabel: 'Dnes' },
  { to: '/plan', number: '02', label: 'Můj plán', shortLabel: 'Plán' },
  { to: '/library', number: '03', label: 'Cviky', shortLabel: 'Cviky' },
  { to: '/history', number: '04', label: 'Historie', shortLabel: 'Historie' },
  { to: '/settings', number: '05', label: 'Nastavení', shortLabel: 'Nastav.' },
]

function isActivePath(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' || pathname.startsWith('/workout/') : pathname.startsWith(to)
}

export function AppShell({ user, children }: { user: AuthenticatedUser; children: React.ReactNode }) {
  const pathname = usePathname()
  const [online, setOnline] = useState(navigator.onLine)
  const [update, setUpdate] = useState<ServiceWorkerRegistration | null>(null)
  const version = useQuery({
    queryKey: ['system-version'],
    queryFn: () => apiRequest<{ version: string }>('/system/version'),
    enabled: online,
    retry: false,
    staleTime: 60 * 60 * 1000,
  })
  const logout = useMutation({
    mutationFn: () => apiRequest<void>('/auth/logout', { method: 'POST' }),
    onSettled: async () => {
      clearVerifiedOfflineSession()
      queryClient.clear()
      navigate('/login', true)
    },
  })

  useEffect(() => {
    const updateReady = (event: Event) => setUpdate((event as CustomEvent<ServiceWorkerRegistration>).detail)
    window.addEventListener('repinu:update-ready', updateReady)
    return () => window.removeEventListener('repinu:update-ready', updateReady)
  }, [])

  useEffect(() => {
    const goOnline = () => setOnline(true)
    const goOffline = () => setOnline(false)
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  return (
    <div className="app-frame">
      {update && (
        <div className="update-banner" role="status">
          <span>Je připravená nová verze RepInu.</span>
          <button type="button" onClick={() => update.waiting?.postMessage({ type: 'SKIP_WAITING' })}>
            <ArrowClockwise size={17} /> Aktualizovat
          </button>
        </div>
      )}

      <aside className="sidebar" aria-label="Hlavní navigace">
        <div className="brand-lockup">
          <img className="brand-mark" src="/assets/repinu-login.png" alt="" aria-hidden="true" />
          <strong>Rep<span>Inu</span></strong>
        </div>
        <nav className="sidebar-nav">
          {navItems.map(({ to, number, label }) => {
            const active = isActivePath(pathname, to)
            return (
              <Link key={to} to={to} className={`nav-link${active ? ' is-active' : ''}`}>
                <span className="nav-link__rule" aria-hidden="true" />
                <span className="nav-link__number">{number}</span>
                <span>{label}</span>
              </Link>
            )
          })}
        </nav>
        <div className="sidebar-footer">
          <div className={`connection-state ${online ? '' : 'is-offline'}`}>
            {online ? <WifiHigh size={16} /> : <WifiSlash size={16} />}
            {online ? 'Online' : 'Offline režim'}
          </div>
          <div className="account-line">
            <div><strong>{user.username}</strong><small>{user.role}</small></div>
            <button
              type="button"
              className="icon-button"
              title="Odhlásit se"
              aria-label="Odhlásit se"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
            >
              <SignOut size={20} />
            </button>
          </div>
          <div className="version-line">
            <span>RepInu {version.data?.version ?? 'vNext'}</span>
            <a href="https://www.shiroji.com" target="_blank" rel="noreferrer">Navrhl Shiroji</a>
          </div>
        </div>
      </aside>

      <header className="mobile-header">
        <img src="/assets/repinu-login.png" alt="" aria-hidden="true" />
        <strong>Rep<span>Inu</span></strong>
        <span className={`mobile-connection ${online ? '' : 'is-offline'}`}>
          {online ? 'Online' : 'Offline'}
        </span>
      </header>

      <main className="main-content" id="main-content">{children}</main>

      <nav className="mobile-nav" aria-label="Mobilní navigace">
        {navItems.map(({ to, number, shortLabel, label }) => {
          const active = isActivePath(pathname, to)
          return (
            <Link key={to} to={to} className={active ? 'is-active' : ''} ariaLabel={label}>
              <span>{number}</span>
              <strong>{shortLabel}</strong>
            </Link>
          )
        })}
      </nav>
    </div>
  )
}